import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Megaphone, Send, Paperclip, X, FileText, Image, File,
  Trash2, ChevronDown, ChevronUp, Bell, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'generale', label: 'Generale', color: 'cyan' },
  { value: 'protocollo', label: 'Protocollo', color: 'blue' },
  { value: 'evento', label: 'Evento', color: 'violet' },
  { value: 'emergenza', label: 'Emergenza', color: 'red' }
];

const AnnouncementAdmin = () => {
  const { token, isAdmin } = useAuth();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('generale');
  const [attachments, setAttachments] = useState([]);
  const [sendNotification, setSendNotification] = useState(true);
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [newAttachmentUrl, setNewAttachmentUrl] = useState('');
  const [newAttachmentName, setNewAttachmentName] = useState('');

  useEffect(() => {
    if (isAdmin) {
      fetchAnnouncements();
    }
  }, [isAdmin]);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/announcements`);
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const handleAddAttachment = () => {
    if (!newAttachmentUrl.trim()) {
      toast.error('Inserisci un URL valido');
      return;
    }
    
    // Genera nome automatico se non inserito
    let name = newAttachmentName.trim();
    if (!name) {
      // Estrai nome dal URL o usa default
      try {
        const url = new URL(newAttachmentUrl);
        const pathParts = url.pathname.split('/').filter(p => p);
        name = pathParts[pathParts.length - 1] || `Allegato ${attachments.length + 1}`;
        // Rimuovi estensione per nome più pulito se troppo lungo
        if (name.length > 30) {
          name = `Allegato ${attachments.length + 1}`;
        }
      } catch {
        name = `Allegato ${attachments.length + 1}`;
      }
    }
    
    const type = getFileType(newAttachmentUrl);
    
    setAttachments([...attachments, { name, url: newAttachmentUrl, type }]);
    setNewAttachmentUrl('');
    setNewAttachmentName('');
    toast.success('Allegato aggiunto!');
  };

  const getFileType = (url) => {
    const lower = url.toLowerCase();
    if (lower.match(/\.(jpg|jpeg|png|gif|webp)$/)) return 'image';
    if (lower.match(/\.(pdf)$/)) return 'pdf';
    if (lower.match(/\.(doc|docx)$/)) return 'document';
    return 'file';
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/announcements`, {
        title,
        body,
        category,
        attachments,
        send_notification: sendNotification
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message);
      setTitle('');
      setBody('');
      setCategory('generale');
      setAttachments([]);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo annuncio?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annuncio eliminato');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'pdf': return <FileText className="w-4 h-4" />;
      default: return <File className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (cat) => {
    const c = CATEGORIES.find(c => c.value === cat);
    return c ? c.color : 'gray';
  };

  if (!isAdmin) return null;

  return (
    <div className="bg-slate-900/60 border border-violet-500/30 rounded-xl p-4 mb-6">
      <h3 className="text-violet-400 font-bold flex items-center gap-2 mb-4">
        <Megaphone className="w-5 h-5" />
        Comunicazioni Community
      </h3>

      {/* Form nuovo annuncio */}
      <div className="space-y-3 mb-6">
        <div>
          <label className="text-xs text-slate-400 uppercase">Titolo *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            placeholder="Es: Nuovo protocollo tartarughe spiaggiate"
          />
        </div>
        
        <div>
          <label className="text-xs text-slate-400 uppercase">Messaggio *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            rows={3}
            placeholder="Scrivi il contenuto della comunicazione..."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 uppercase">Categoria</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer p-2">
              <input
                type="checkbox"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className="w-4 h-4 rounded text-violet-500"
              />
              <span className="text-slate-300 text-sm flex items-center gap-1">
                <Bell className="w-4 h-4" />
                Invia notifica push
              </span>
            </label>
          </div>
        </div>

        {/* Allegati */}
        <div className="border border-dashed border-slate-600 rounded-lg p-3">
          <p className="text-xs text-slate-400 uppercase mb-2 flex items-center gap-1">
            <Paperclip className="w-3 h-3" />
            Allegati (URL di file o immagini)
          </p>
          
          <div className="space-y-2 mb-2">
            <input
              type="url"
              value={newAttachmentUrl}
              onChange={(e) => setNewAttachmentUrl(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
              placeholder="https://drive.google.com/file/... oppure https://esempio.com/documento.pdf"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={newAttachmentName}
                onChange={(e) => setNewAttachmentName(e.target.value)}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                placeholder="Nome file (opzionale, es: Protocollo Tartarughe)"
              />
              <button
                onClick={handleAddAttachment}
                className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-lg text-sm font-medium"
              >
                Aggiungi
              </button>
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-700">
              {attachments.map((att, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-slate-700 rounded-lg text-sm text-slate-300 flex items-center gap-2">
                  {getFileIcon(att.type)}
                  {att.name}
                  <button onClick={() => removeAttachment(idx)} className="text-red-400 hover:text-red-300">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-2">
            Carica i file su Google Drive, Dropbox o simili e incolla il link
          </p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              Pubblica Comunicazione
            </>
          )}
        </button>
      </div>

      {/* Lista annunci esistenti */}
      {announcements.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 uppercase mb-2">Annunci Pubblicati ({announcements.length})</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {announcements.slice(0, 10).map((a) => (
              <div key={a.id} className="bg-slate-800/50 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded bg-${getCategoryColor(a.category)}-500/20 text-${getCategoryColor(a.category)}-400`}>
                        {CATEGORIES.find(c => c.value === a.category)?.label || a.category}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(a.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                    <p className="text-white font-medium text-sm">{a.title}</p>
                    {a.attachments?.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">
                        <Paperclip className="w-3 h-3 inline mr-1" />
                        {a.attachments.length} allegat{a.attachments.length > 1 ? 'i' : 'o'}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementAdmin;
